import React, { useState, useEffect } from 'react';
import { Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react';
import P1 from '../../assets/images/p1.png';
import P2 from '../../assets/images/p2.png';
import P3 from '../../assets/images/p3.png';

const Testimonials = () => {
    const [activeIndex, setActiveIndex] = useState(0);
    const [autoplay, setAutoplay] = useState(true);

    const testimonials = [
        {
            id: 1,
            name: "Sarah Miller",
            location: "Kreuzberg",
            image: P2,
            rating: 5,
            text: "BerlinTiffin has become my weekday lifesaver! The food is authentic, flavorful, and reminds me of the home cooking I experienced while traveling in India. Their delivery is always on time and the portions are generous. Highly recommend the Butter Chicken and Palak Paneer!"
        },
        {
            id: 2,
            name: "Marcel Schmidt",
            location: "Mitte",
            image: P1,
            rating: 5,
            text: "I've tried many Indian food delivery services in Berlin, but none compare to BerlinTiffin. The freshness of ingredients really stands out, and you can taste the care that goes into each dish. Their subscription plan is perfect for busy professionals like me."
        },
        {
            id: 3,
            name: "Thomas Weber",
            location: "Neukölln",
            image: P3,
            rating: 4,
            text: "What impressed me most about BerlinTiffin is how they maintain quality even during busy lunch hours. The food arrives hot, well-packaged, and always delicious. Their lunch combos offer great value for money too!"
        }
    ];

    // Autoplay functionality
    useEffect(() => {
        let interval;
        if (autoplay) {
            interval = setInterval(() => {
                setActiveIndex((current) => (current + 1) % testimonials.length);
            }, 5000); // Change slide every 5 seconds
        }
        return () => clearInterval(interval);
    }, [autoplay, testimonials.length]);

    // Navigation functions
    const goToPrevious = () => {
        setActiveIndex((current) => (current === 0 ? testimonials.length - 1 : current - 1));
        setAutoplay(false); // Stop autoplay when user interacts
    };

    const goToNext = () => {
        setActiveIndex((current) => (current + 1) % testimonials.length);
        setAutoplay(false); // Stop autoplay when user interacts
    };

    // Star rating component
    const StarRating = ({ rating }) => {
        return (
            <div className="flex">
                {[...Array(5)].map((_, i) => (
                    <Star
                        key={i}
                        size={16}
                        className={`${i < rating ? 'fill-current' : 'text-gray-300'
                            }`}
                        style={i < rating ? { color: '#ffe500' } : {}}
                    />
                ))}
            </div>
        );
    };

    // Testimonial card component
    const TestimonialCard = ({ testimonial }) => {
        return (
            <div className="bg-white rounded-lg shadow-md p-8 relative mx-auto max-w-3xl">
                {/* Quote icon */}
                <div className="absolute -top-3 -left-3 rounded-full p-2 shadow-md" style={{ backgroundColor: '#7bae37' }}>
                    <Quote size={20} className="text-white" />
                </div>

                <div className="mb-4">
                    <StarRating rating={testimonial.rating} />
                </div>

                <p className="text-gray-700 italic mb-8 text-lg leading-relaxed">"{testimonial.text}"</p>

                <div className="flex items-center">
                    <img
                        src={testimonial.image}
                        alt={testimonial.name}
                        className="w-16 h-16 rounded-full object-cover border-2"
                        style={{ borderColor: '#7bae37' }}
                    />
                    <div className="ml-4">
                        <h4 className="font-bold text-gray-800 text-lg">{testimonial.name}</h4>
                        <p className="text-gray-600">{testimonial.location}, Berlin</p>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <section className="py-16 bg-zinc-100">
            <div className="container mx-auto px-4">
                {/* Section header */}
                <div className="text-white p-6 rounded-lg shadow-lg mb-12 max-w-2xl mx-auto text-center" style={{ backgroundColor: '#7bae37' }}>
                    <h2 className="text-2xl font-bold">What Our Customers Say</h2>
                    <p className="opacity-90 mt-1">Read testimonials from our satisfied customers across Berlin</p>
                </div>

                {/* Testimonials Carousel */}
                <div className="relative max-w-5xl mx-auto">
                    <div className="overflow-hidden">
                        <div
                            className="transition-transform duration-500 ease-in-out"
                            style={{ transform: `translateX(-${activeIndex * 100}%)` }}
                        >
                            <div className="flex">
                                {testimonials.map((testimonial) => (
                                    <div key={testimonial.id} className="w-full flex-shrink-0">
                                        <TestimonialCard testimonial={testimonial} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Navigation arrows */}
                    <button
                        onClick={goToPrevious}
                        className="absolute top-1/2 -left-4 -translate-y-1/2 bg-white p-2 rounded-full shadow-md hover:bg-gray-100 focus:outline-none z-10"
                        aria-label="Previous testimonial"
                    >
                        <ChevronLeft style={{ color: '#7bae37' }} size={24} />
                    </button>

                    <button
                        onClick={goToNext}
                        className="absolute top-1/2 -right-4 -translate-y-1/2 bg-white p-2 rounded-full shadow-md hover:bg-gray-100 focus:outline-none z-10"
                        aria-label="Next testimonial"
                    >
                        <ChevronRight style={{ color: '#7bae37' }} size={24} />
                    </button>

                    {/* Indicator dots */}
                    <div className="flex justify-center mt-8">
                        {testimonials.map((_, index) => (
                            <button
                                key={index}
                                onClick={() => {
                                    setActiveIndex(index);
                                    setAutoplay(false);
                                }}
                                className={`h-2.5 w-2.5 mx-1 rounded-full ${activeIndex === index ? '' : 'bg-gray-300'
                                    }`}
                                style={activeIndex === index ? { backgroundColor: '#7bae37' } : {}}
                                aria-label={`Go to testimonial ${index + 1}`}
                            />
                        ))}
                    </div>
                </div>

                {/* Call to action */}
                {/* <div className="mt-12 text-center">
                    <p className="text-gray-700 mb-4">Join over 1,200+ satisfied customers enjoying authentic Indian meals</p>
                    <button className="px-8 py-3 text-white rounded-md font-semibold transition-colors shadow-md" style={{backgroundColor: '#7bae37'}}>
                        Order Your Tiffin Today
                    </button>
                </div> */}
            </div>
        </section>
    );
};

export default Testimonials;